import {useMemo} from 'react';

import {ClassLabelMap} from '../../../util/media';
import {RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {Layout, MediaConfigWithDefaults} from '../Component';
import {
  adaptGridDataToLayout,
  buildSlotRegistry,
  compareArchitectures,
  dispatchTransform,
  logComparisonResult,
  TransformInput,
} from '../layout';
import {makeLayout} from '../makeLayout';
import {StepMeta} from '../types';

export type LayoutGenerationInput = {
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  stepMeta: StepMeta;
  currentSliderValue: number;
  allMasks: ClassLabelMap;
  configWithDefaults: MediaConfigWithDefaults;
};

/**
 * Generates the layout structure (tiles, axes, etc.) based on run data and config.
 *
 * This is the integration point for the new layout architecture.
 * Currently uses the existing makeLayout function, but can be migrated to:
 * - transformForGallery()
 * - transformForGrid()
 * - transformForCompare()
 *
 * @param input - Run data, step metadata, and configuration
 * @returns Layout with tiles, axis data, and metadata
 */
export function useLayoutGeneration(input: LayoutGenerationInput): Layout {
  const {
    runsWithMedia,
    stepMeta,
    currentSliderValue,
    allMasks,
    configWithDefaults,
  } = input;

  // Map runs to the format expected by makeLayout
  const mappedRuns = useMemo(() => {
    return runsWithMedia.map(run => ({
      name: run.name,
      displayName: run.displayName,
      entityName: run.entityName,
      projectName: run.projectName,
      history: run.history,
      notes: run.notes,
      color: run.color,
      _wandb: run._wandb,
    }));
  }, [runsWithMedia]);

  // Feature flags for gradual migration
  const LOG_ARCHITECTURE_COMPARISON = true; // Log old vs new comparison (requires new arch enabled)

  // Generate layout structure
  const layout = useMemo(() => {
    // Early return if we don't have the required data
    if (runsWithMedia.length === 0 || stepMeta.runs.length === 0) {
      return {tiles: []};
    }

    try {
      // Prepare input for transform
      const transformInput: TransformInput = {
        runs: mappedRuns,
        stepMeta,
        currentStep: currentSliderValue,
        currentIndex: configWithDefaults.mediaIndex ?? 0,
        config: configWithDefaults,
        allMasks,
        allBoxes: {}, // TODO: Add box data when available
      };

      // Dispatch to appropriate transform
      const gridData = dispatchTransform(transformInput);

      // Adapt to existing Layout structure
      const newLayout = adaptGridDataToLayout(gridData);

      // NEW: Generate slot registry from layout strategy
      const slots = buildSlotRegistry(configWithDefaults);

      // Optional: Log comparison with old architecture for validation
      if (LOG_ARCHITECTURE_COMPARISON) {
        const comparisonResult = compareArchitectures({
          runs: mappedRuns,
          stepMeta,
          currentStep: currentSliderValue,
          currentIndex: configWithDefaults.mediaIndex ?? 0,
          config: configWithDefaults,
          allMasks,
        });
        logComparisonResult(comparisonResult, 'Layout Architecture');
      }

      return {
        ...newLayout,
        slots, // Add slots to layout output
        gridData, // Add gridData for new rendering architecture
      };
    } catch (error) {
      console.error('New layout architecture failed, falling back:', error);
      // Fall back to old implementation on error
      return makeLayout({
        runs: mappedRuns,
        stepMeta,
        config: configWithDefaults,
        allMasks,
        currentStep: currentSliderValue,
      });
    }
  }, [
    mappedRuns,
    stepMeta,
    configWithDefaults,
    allMasks,
    currentSliderValue,
    runsWithMedia.length,
    LOG_ARCHITECTURE_COMPARISON,
  ]);

  return layout;
}
